import { TableRowsCollapse } from 'uikit';
import type { TableColumn, TableRow } from 'uikit';

import * as R from 'ramda';
import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createGetSelection } from '../app/selectors';
import { selectionSlice } from '../features/selection/selectionSlice';
import { actionBinder } from '../utils/actionBinder';

export const useTableCollapse = <T extends TableRow>(
    instanceId: string,
    columns: TableColumn<T>[],
    rows: T[],
    collapseAccessor?: string,
): [TableColumn<T>[], T[], (id: number) => () => void, (shouldExpand?: boolean) => () => void] => {
    const dispatch = useDispatch();
    const [getSelectionSelector, expand, collapse, setCollapsed] = useMemo(
        () => [
            createGetSelection(instanceId),
            actionBinder(selectionSlice.actions.select, instanceId),
            actionBinder(selectionSlice.actions.unselect, instanceId),
            actionBinder(selectionSlice.actions.set, instanceId),
        ],
        [instanceId],
    );
    const selection = useSelector(getSelectionSelector);

    const isExpanded = useCallback((id: string | number) => selection[id] ?? true, [selection]);

    const toggleCollapse = useCallback(
        (id) => () => {
            const action = isExpanded(id) ? collapse : expand;

            dispatch(action(id));
        },
        [collapse, dispatch, expand, isExpanded],
    );

    const collapseAll = useCallback(
        (shouldExpand = false) =>
            () => {
                const entries = rows.flat().map(({ id }) => [id, shouldExpand]);

                dispatch(setCollapsed(Object.fromEntries(entries)));
            },
        [dispatch, rows, setCollapsed],
    );

    const addCollapseProps = useCallback(
        (levelRows: T[], level: number) =>
            levelRows.reduce(
                (acc, row) => {
                    if (row.rows && row.rows.length > 0) {
                        const isRowExpanded = isExpanded(row.id);

                        // @ts-ignore
                        acc.push({
                            ...R.omit(['rows'], row),
                            isExpanded: isRowExpanded,
                            nestLevel: level,
                            withCollapseButton: true,
                        });

                        if (isRowExpanded) {
                            acc = acc.concat(addCollapseProps(row.rows as T[], level + 1));
                        }
                    } else {
                        acc.push({ ...row, nestLevel: level });
                    }

                    return acc;
                },
                [] as unknown as T[] & { nestLevel?: number; isExpanded?: boolean; withCollapseButton?: boolean },
            ),
        [isExpanded],
    );

    const finalRows: T[] = useMemo(() => addCollapseProps(rows, 0), [addCollapseProps, rows]);

    const finalColumns = useMemo(() => {
        const [column, ...restColumns] = columns;

        return [
            {
                ...column,
                renderCell: (row: any) => {
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                    const cellContent = column.renderCell ? column.renderCell(row) : row[column.accessor!];

                    return (
                        <TableRowsCollapse
                            level={row.nestLevel}
                            isExpanded={row.isExpanded}
                            withCollapseButton={row.withCollapseButton}
                            toggleCollapse={toggleCollapse(row.id)}
                        >
                            {collapseAccessor ? row[collapseAccessor] : cellContent}
                        </TableRowsCollapse>
                    );
                },
            },
            ...restColumns,
        ];
    }, [collapseAccessor, columns, toggleCollapse]);

    return [finalColumns, finalRows, toggleCollapse, collapseAll];
};
